// Mock data for the prototype. Real backend (Phase 3+) replaces this entirely.
import type {
  AgeGroup,
  Booking,
  Inquiry,
  Provider,
  ProviderClassroom,
  Schedule,
  Tone,
  WaitlistEntry,
} from './types';

export const AGE_GROUPS: readonly AgeGroup[] = [
  'Infant',
  'Young Toddler',
  'Older Toddler',
  'Pre-K',
  'School Age',
] as const;

export const AGE_GROUP_RANGES: Record<AgeGroup, string> = {
  Infant: '0–12 mo',
  'Young Toddler': '1–2 yr',
  'Older Toddler': '2–3 yr',
  'Pre-K': '3–5 yr',
  'School Age': '6–13 yr',
};

export const AGE_GROUP_INITIALS: Record<AgeGroup, string> = {
  Infant: 'I',
  'Young Toddler': 'YT',
  'Older Toddler': 'OT',
  'Pre-K': 'PK',
  'School Age': 'SA',
};

export const SCHEDULES: readonly Schedule[] = ['Full time', 'Part time'] as const;

// Tone -> placeholder color block. LEGO-bright and intentionally flat.
export const TONE_BG: Record<Tone, string> = {
  A: '#FFD13A',
  B: '#3FB46A',
  C: '#4189E4',
  D: '#EE5547',
  E: '#FF9F45',
  F: '#8E63CC',
};

export const PROVIDERS: Provider[] = [
  {
    id: 'sunnyside',
    name: 'Sunnyside Learning Garden',
    neighborhood: 'Fairmount',
    zip: '19130',
    distance: '0.4 mi',
    address: '2418 Brown Street, Philadelphia, PA',
    status: 'open',
    spots: 4,
    ages: ['Infant', 'Young Toddler', 'Older Toddler', 'Pre-K'],
    schedules: ['Full time', 'Part time'],
    subsidy: true,
    rating: 4.9,
    reviews: 128,
    tone: 'A',
    image: '/assets/image1.webp',
    gallery: [
      { label: 'Classroom', src: '/assets/image7.webp' },
      { label: 'Garden', src: '/assets/image8.webp' },
      { label: 'Art corner', src: '/assets/image9.webp' },
      { label: 'Naptime', src: '/assets/image10.webp' },
    ],
    description:
      'A play-based home center on a quiet block in Fairmount. Two outdoor gardens, family-style meals, and small mixed-age groups led by educators who have been with us for years.',
    availability: {
      Infant: { spots: 2, note: 'Starts Jun 2' },
      'Young Toddler': { spots: 1, note: 'Starts Jul 1' },
      'Older Toddler': { spots: 1, note: 'Starts Jul 1' },
      'Pre-K': { spots: 0, note: 'Waitlist · 4 ahead' },
      'School Age': { spots: 0, note: 'Not offered' },
    },
    availabilityUpdatedAt: '2026-09-27T18:00:00.000Z',
    hours: 'Mon–Fri · 7:30 AM – 5:30 PM',
    languages: 'English, Spanish',
  },
  {
    id: 'cedar-house',
    name: 'Cedar House Childcare',
    neighborhood: 'Northern Liberties',
    zip: '19123',
    distance: '1.1 mi',
    address: '914 N 4th Street, Philadelphia, PA',
    status: 'open',
    spots: 2,
    ages: ['Young Toddler', 'Older Toddler', 'Pre-K'],
    schedules: ['Full time'],
    subsidy: true,
    rating: 4.8,
    reviews: 94,
    tone: 'B',
    image: '/assets/image2.webp',
    description:
      'Reggio-inspired classrooms with a focus on outdoor exploration and family partnerships.',
    availability: {
      Infant: { spots: 0, note: 'Not offered' },
      'Young Toddler': { spots: 1, note: 'Starts immediately' },
      'Older Toddler': { spots: 0, note: 'Waitlist · 2 ahead' },
      'Pre-K': { spots: 1, note: 'Starts Aug 18' },
      'School Age': { spots: 0, note: 'Waitlist · 2 ahead' },
    },
    hours: 'Mon–Fri · 7:00 AM – 6:00 PM',
    languages: 'English',
  },
  {
    id: 'little-rivers',
    name: 'Little Rivers Early Center',
    neighborhood: 'Queen Village',
    zip: '19147',
    distance: '1.6 mi',
    address: '602 Christian Street, Philadelphia, PA',
    status: 'open',
    spots: 1,
    ages: ['Infant'],
    schedules: ['Full time', 'Part time'],
    subsidy: false,
    rating: 4.7,
    reviews: 71,
    tone: 'C',
    image: '/assets/image3.webp',
    description:
      'Small-ratio infant-only program. Quiet, low-stim space designed by a pediatric OT.',
    availability: {
      Infant: { spots: 1, note: 'Starts Jun 16' },
      'Young Toddler': { spots: 0, note: 'Not offered' },
      'Older Toddler': { spots: 0, note: 'Not offered' },
      'Pre-K': { spots: 0, note: 'Not offered' },
      'School Age': { spots: 0, note: 'Not offered' },
    },
    hours: 'Mon–Fri · 8:00 AM – 5:00 PM',
    languages: 'English, Mandarin',
  },
  {
    id: 'morning-glory',
    name: 'Morning Glory Co-op',
    neighborhood: 'Bella Vista',
    zip: '19147',
    distance: '0.9 mi',
    address: '811 S 8th Street, Philadelphia, PA',
    status: 'waitlist',
    spots: 0,
    ages: ['Young Toddler', 'Older Toddler', 'Pre-K', 'School Age'],
    schedules: ['Full time', 'Part time'],
    subsidy: true,
    rating: 4.9,
    reviews: 211,
    tone: 'D',
    image: '/assets/image4.webp',
    description:
      'Parent-cooperative program with a music studio and weekly forest school field trips.',
    availability: {
      Infant: { spots: 0, note: 'Not offered' },
      'Young Toddler': { spots: 0, note: 'Waitlist · 3 ahead' },
      'Older Toddler': { spots: 0, note: 'Waitlist · 3 ahead' },
      'Pre-K': { spots: 0, note: 'Waitlist · 6 ahead' },
      'School Age': { spots: 0, note: 'Waitlist · 1 ahead' },
    },
    hours: 'Mon–Fri · 8:00 AM – 5:30 PM',
    languages: 'English, French',
  },
  {
    id: 'oak-and-acorn',
    name: 'Oak & Acorn Children\u2019s House',
    neighborhood: 'Graduate Hospital',
    zip: '19146',
    distance: '1.3 mi',
    address: '1601 South Street, Philadelphia, PA',
    status: 'open',
    spots: 3,
    ages: ['Infant', 'Young Toddler', 'Older Toddler'],
    schedules: ['Full time'],
    subsidy: false,
    rating: 4.8,
    reviews: 64,
    tone: 'E',
    image: '/assets/image5.webp',
    description:
      'Montessori-inspired program in a converted carriage house with a hidden courtyard.',
    availability: {
      Infant: { spots: 2, note: 'Starts Jun 9' },
      'Young Toddler': { spots: 1, note: 'Starts immediately' },
      'Older Toddler': { spots: 0, note: 'Waitlist · 2 ahead' },
      'Pre-K': { spots: 0, note: 'Not offered' },
      'School Age': { spots: 0, note: 'Not offered' },
    },
    hours: 'Mon–Fri · 7:45 AM – 5:30 PM',
    languages: 'English',
  },
  {
    id: 'maple-street',
    name: 'Maple Street Kids',
    neighborhood: 'Point Breeze',
    zip: '19146',
    distance: '2.0 mi',
    address: '1230 S 22nd Street, Philadelphia, PA',
    status: 'waitlist',
    spots: 0,
    ages: ['Infant', 'Young Toddler', 'Older Toddler', 'Pre-K'],
    schedules: ['Full time', 'Part time'],
    subsidy: true,
    rating: 4.6,
    reviews: 142,
    tone: 'F',
    image: '/assets/image6.webp',
    description: 'Bilingual full-day program with rooftop garden, in operation since 2009.',
    availability: {
      Infant: { spots: 0, note: 'Waitlist · 5 ahead' },
      'Young Toddler': { spots: 0, note: 'Waitlist · 2 ahead' },
      'Older Toddler': { spots: 0, note: 'Waitlist · 2 ahead' },
      'Pre-K': { spots: 0, note: 'Waitlist · 4 ahead' },
      'School Age': { spots: 0, note: 'Not offered' },
    },
    hours: 'Mon–Fri · 7:00 AM – 6:00 PM',
    languages: 'English, Spanish',
  },
];

export const PROVIDER_CLASSROOMS: ProviderClassroom[] = [
  {
    id: 'room-sunbeam',
    providerId: 'sunnyside',
    name: 'Sunbeam Room',
    ageGroup: 'Infant',
    capacity: 8,
    enrolled: 6,
    openings: 2,
    schedule: 'Full time',
    openingDate: '2026-10-05',
    waitlistCount: 3,
  },
  {
    id: 'room-meadow',
    providerId: 'sunnyside',
    name: 'Meadow Room',
    ageGroup: 'Young Toddler',
    capacity: 10,
    enrolled: 9,
    openings: 1,
    schedule: 'Full time',
    openingDate: '2026-11-02',
    waitlistCount: 2,
  },
  {
    id: 'room-garden',
    providerId: 'sunnyside',
    name: 'Garden Room',
    ageGroup: 'Older Toddler',
    capacity: 12,
    enrolled: 11,
    openings: 1,
    schedule: 'Part time',
    openingDate: '2026-10-19',
    waitlistCount: 2,
  },
  {
    id: 'room-bluebird',
    providerId: 'sunnyside',
    name: 'Bluebird Room',
    ageGroup: 'Pre-K',
    capacity: 16,
    enrolled: 16,
    openings: 0,
    schedule: 'Full time',
    openingDate: '',
    waitlistCount: 4,
  },
];

export const INQUIRIES: Inquiry[] = [
  {
    id: 1,
    name: 'Priya R.',
    age: 'Young Toddler',
    type: 'Virtual',
    date: 'Tue, May 19 · 4:30 PM',
    phone: '(215) 555-0142',
    status: 'new',
  },
  {
    id: 2,
    name: 'Marcus W.',
    age: 'Infant',
    type: 'In Person',
    date: 'Wed, May 20 · 10:00 AM',
    phone: '(267) 555-0119',
    status: 'confirmed',
  },
  {
    id: 3,
    name: 'Hana K.',
    age: 'Pre-K',
    type: 'In Person',
    date: 'Thu, May 21 · 9:00 AM',
    phone: '(484) 555-0181',
    status: 'new',
  },
  {
    id: 4,
    name: 'Diego A.',
    age: 'Older Toddler',
    type: 'Virtual',
    date: 'Fri, May 22 · 1:00 PM',
    phone: '(215) 555-0177',
    status: 'new',
  },
  {
    id: 5,
    name: 'Bea & Sam L.',
    age: 'Infant',
    type: 'In Person',
    date: 'Mon, May 25 · 11:30 AM',
    phone: '(610) 555-0162',
    status: 'confirmed',
  },
];

export const BOOKINGS: Booking[] = [
  {
    id: 'tour-maya',
    providerId: 'sunnyside',
    parentName: 'Maya R.',
    childAge: 'Pre-K',
    tourType: 'in-person',
    date: 'Thursday, October 1',
    time: '10:00 AM',
    phone: '(215) 555-0168',
    createdAt: '2026-09-27T15:15:00.000Z',
    status: 'pending',
  },
];

export const WAITLIST_ENTRIES: WaitlistEntry[] = [
  {
    id: 'waitlist-nguyen',
    providerId: 'sunnyside',
    parentName: 'Nguyen family',
    childAge: 'Infant',
    phone: '(215) 555-0134',
    joinedAt: '2026-09-22T14:30:00.000Z',
    status: 'waiting',
  },
];
